import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { workflowApi } from '../services/api';

interface FormData {
  workflowType: string;
  input: string;
  schedule?: string;
}

const CreateWorkflow: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    workflowType: '',
    input: '{\n  "message": "Hello World",\n  "timestamp": "' + new Date().toISOString() + '"\n}',
    schedule: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Validate JSON input
      let inputData;
      try {
        inputData = JSON.parse(formData.input);
      } catch (jsonError) {
        throw new Error('Invalid JSON format in input data');
      }

      const result = await workflowApi.createWorkflow({
        workflowType: formData.workflowType,
        input: inputData,
        schedule: formData.schedule || undefined
      });

      if (result && result.success) {
        navigate(`/workflows/${encodeURIComponent(result.data.executionArn)}`);
      } else {
        throw new Error((result && result.error) || 'Failed to create workflow');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while creating the workflow');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">Create Workflow</h1>
          <p className="mt-1 text-sm text-gray-500">
            Start a new workflow execution with custom parameters
          </p>
        </div>

        <div className="px-6 py-4">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Workflow Type */}
            <div>
              <label htmlFor="workflowType" className="block text-sm font-medium text-gray-700">
                Workflow Type *
              </label>
              <select
                id="workflowType"
                value={formData.workflowType}
                onChange={(e) => handleInputChange('workflowType', e.target.value)}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a workflow type</option>
                <option value="data-processing">Data Processing</option>
                <option value="notification">Notification</option>
                <option value="batch-job">Batch Job</option>
              </select>
            </div>

            {/* Input Data */}
            <div>
              <label htmlFor="input" className="block text-sm font-medium text-gray-700">
                Input Data (JSON) *
              </label>
              <textarea
                id="input"
                value={formData.input}
                onChange={(e) => handleInputChange('input', e.target.value)}
                required
                rows={8}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                placeholder='{"message": "Hello World"}'
              />
              <p className="mt-1 text-sm text-gray-500">
                Provide the input data for your workflow in JSON format
              </p>
            </div>

            {/* Schedule (Optional) */}
            <div>
              <label htmlFor="schedule" className="block text-sm font-medium text-gray-700">
                Schedule (Optional)
              </label>
              <input
                type="text"
                id="schedule"
                value={formData.schedule}
                onChange={(e) => handleInputChange('schedule', e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="rate(1 hour) or cron(0 9 * * ? *)"
              />
              <p className="mt-1 text-sm text-gray-500">
                Leave empty for immediate execution, or provide a schedule expression
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <p className="mt-1 text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate('/workflows')}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isSubmitting ? 'Creating...' : 'Create Workflow'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateWorkflow;
